import React, { useState } from 'react';
import { AiFillStar, AiOutlineStar } from 'react-icons/ai';
import { IoMdSunny, IoMdMoon, IoMdClock } from 'react-icons/io';
import { VscDash } from 'react-icons/vsc';
import classNames from 'styles/utils/classNames';
import { TickerCell } from '../components/TickerCell';
import { TickerType } from '../types';
import {
  formatEPS,
  formatMarketCap,
  CustomColumnDef,
  formatPercentage,
  calculateSurprisePercentage,
  formatNumber,
  getTextColorClass,
  getTextColorClassPercent,
} from 'screens/Calendar';

const Tooltip: React.FC<{ children: React.ReactNode; content: string }> = ({
  children,
  content,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {isVisible && (
        <div className="absolute bottom-full left-1/2 z-50 mb-2 -translate-x-1/2 transform">
          <div className="whitespace-nowrap rounded border border-gray-700 bg-gray-900 px-2 py-1 text-xs text-white shadow-lg">
            {content}
            <div className="absolute left-1/2 top-full h-0 w-0 -translate-x-1/2 transform border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export const renderTimeIconWithTooltip = (timeType: string) => {
  if (!timeType || timeType === '-') {
    return (
      <Tooltip content="No timing information available">
        <VscDash className="ml-1 text-gray-400" />
      </Tooltip>
    );
  }

  if (timeType.includes('BM')) {
    return (
      <Tooltip content="Before Market Opens">
        <IoMdSunny className="ml-1 text-yellow-400" />
      </Tooltip>
    );
  }

  if (timeType.includes('AM')) {
    return (
      <Tooltip content="After Market Closes">
        <IoMdMoon className="ml-1 text-blue-400" />
      </Tooltip>
    );
  }

  if (timeType.includes('DM')) {
    return (
      <Tooltip content="During Market Hours">
        <IoMdClock className="ml-1 text-gray-300" />
      </Tooltip>
    );
  }

  return (
    <Tooltip content={timeType}>
      <VscDash className="ml-1 text-gray-400" />
    </Tooltip>
  );
};

const calculateSurprise = (actual: number | null, estimated: number | null): number | null => {
  if (actual == null || estimated == null) return null;
  return actual - estimated;
};

export const createEarningsColumns = (
  bookmarkedItems: Set<string>,
  handleBookmark: (symbol: string) => void,
  tickerData: TickerType[] = [],
  slug?: string,
): CustomColumnDef<any>[] => [
    {
      accessorKey: 'symbol',
      header: 'Ticker',
      cell: ({ row }) => {
        const ticker = row.original.symbol;
        const isBookmarked = bookmarkedItems.has(ticker);

        return (
          <div className="group/row relative">
            <div className="group relative ml-2">
              <button
                onClick={() => handleBookmark(ticker)}
                className={classNames(
                  'absolute -left-[20px] top-1/2 z-10 -translate-y-1/2 p-1 transition-all duration-200',
                  'group-hover/row:opacity-100',
                  isBookmarked
                    ? 'text-yellow-400 opacity-100'
                    : 'text-gray-400 opacity-70 hover:text-yellow-400 hover:opacity-100',
                )}
              >
                {isBookmarked ? (
                  <AiFillStar className="h-3 w-3" />
                ) : (
                  <>
                    <AiOutlineStar className="h-3 w-3 group-hover:hidden" />
                    <AiFillStar className="hidden h-3 w-3 group-hover:block" />
                  </>
                )}
              </button>
              <TickerCell ticker={ticker} tickerData={tickerData} slug={slug} />
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'marketCap',
      header: 'Market Cap',
      cell: ({ row }) => formatMarketCap(row.original.marketCap),
      csvType: 'currency-large',
    },
    {
      accessorKey: 'date',
      header: 'Earnings Date',
      csvType: 'date',
      cell: ({ row }) => {
        const date = row.original.date;
        const timeType = row.original.time;

        return (
          <div className="flex items-center">
            <span className="mr-2">{date || '--'}</span>
            {renderTimeIconWithTooltip(timeType)}
          </div>
        );
      },
    },
    {
      accessorKey: 'epsEstimated',
      header: 'Forecasted EPS',
      csvType: 'eps',
      cell: ({ row }) => {
        return <span>{formatEPS(row.original.epsEstimated)}</span>;
      },
    },
    {
      accessorKey: 'epsActual',
      header: 'Reported EPS',
      csvType: 'eps',
      cell: ({ row }) => {
        return <span>{formatEPS(row.original.epsActual)}</span>;
      },
    },
    {
      id: 'surprise',
      header: 'Surprise',
      csvType: 'eps',
      accessorFn: (row) => calculateSurprise(row.epsActual, row.epsEstimated),
      cell: ({ getValue }) => {
        const surprise = getValue() as number | null;
        if (surprise == null) return '--';

        return <span className={getTextColorClass(surprise, 2)}>{formatNumber(surprise, 2)}</span>;
      },
    },
    {
      id: 'surprisePercentage',
      header: 'Surprise %',
      csvType: 'percentage-formatted',
      accessorFn: (row) => calculateSurprisePercentage(row.epsActual, row.epsEstimated),
      cell: ({ getValue }) => {
        const surprisePct = getValue() as number | null;
        if (surprisePct == null) return '--';

        return (
          <span className={getTextColorClassPercent(surprisePct)}>
            {formatPercentage(surprisePct, 1)}
          </span>
        );
      },
    },
    {
      accessorKey: 'revenueEstimated',
      header: 'Forecasted Revenue',
      csvType: 'currency-large',
      cell: ({ row }) => formatMarketCap(row.original.revenueEstimated),
    },
    {
      accessorKey: 'revenueActual',
      header: 'Reported Revenue',
      csvType: 'currency-large',
      cell: ({ row }) => formatMarketCap(row.original.revenueActual),
    },
    {
      accessorKey: 'priceTargetTrend',
      header: 'Price Target Trend',
      csvType: 'percentage-formatted',
      cell: ({ row }) => {
        const trend = row.original?.priceTargetTrend;

        if (trend === '-' || trend === null || trend === undefined) {
          return <span className="text-gray-400">-</span>;
        }

        const trendValue = typeof trend === 'string' ? parseFloat(trend) : trend;

        if (isNaN(trendValue)) {
          return <span className="text-gray-400">-</span>;
        }

        return (
          <span className={classNames(trendValue > 0 ? 'text-green-500' : 'text-red-500')}>
            {formatPercentage(trendValue)}
          </span>
        );
      },
    },
    {
      accessorKey: 'consensus',
      header: 'Analyst Rating',
      cell: ({ row }) => {
        const rating = row.original.consensus;

        const color =
          rating === 'Strong Buy' || rating === 'Buy'
            ? 'text-green-500'
            : rating === 'Hold'
              ? 'text-yellow-500'
              : rating === 'Strong Sell' || rating === 'Sell'
                ? 'text-red-500'
                : 'text-gray-400';

        return <span className={color}>{rating ?? '--'}</span>;
      },
    },
  ];
